import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdEdit, MdAddCircle, MdFilterList, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from '@mui/material';
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

// NEW: imports for Excel & PDF export
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFilePdf } from "react-icons/fa";
import excelIcon from "../../assets/icons/excel.png";

const Employeeattendance = () => {
    const [levels, setLevels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const navigate = useNavigate();
    const userData = sessionStorage.getItem('user');
    const userObj = JSON.parse(userData);
    const [users, setUsers] = useState([]);
    const [attendancetypes, setAttendancetype] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [form, setForm] = useState({
        userid: null,
        in_time: null,
        out_time: null,
        attendance_date: null,
        attendance_type_id: null,
        from_date: null,
        to_date: null,
        shift_id: null,
        school_id: "",
        action: "FILTER"
    });

    useEffect(() => {
        setIsLoading(true);
        fetchData().finally(() => setIsLoading(false));
        usersData();
        attendancetypeData();
        shiftData();
    }, []);

    const handleEditClick = (level_id) => {
        const employeemasterToEdit = levels.find(level => level.level_id === level_id);

        if (employeemasterToEdit) {
            navigate("/addlevel", { state: { levelsData: employeemasterToEdit } });
        } else {
            console.error(`Subject with ID ${level_id} not found.`);
        }
    };

    const fetchData = async () => {
        try {
            const response = await axios.post(baseUrl + "/employeeattendance/", {
                action: "READ",
                school_id: userObj.school_id
            });

            console.log("Employee Attendance Response:", response.data);
            setLevels(response.data);
            console.log(levels);

        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    const usersData = async () => {
        try {
            const response = await axios.post(baseUrl + "/employees/", {
                action: "READ",
                school_id: userObj.school_id
            });
            setUsers(response.data);
        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    const attendancetypeData = async () => {
        try {
            const response = await axios.get(baseUrl + "/attendance-types/", {
                action: "READ",
                school_id: userObj.school_id
            });
            setAttendancetype(response.data);
        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    const shiftData = async () => {
        try {
            const response = await axios.post(baseUrl + "/shiftread/", {
                action: "READ",
                school_id: userObj.school_id
            });
            setShifts(response.data);
        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    // Helper to format date consistently (dd-mm-yyyy)
    const formatDateDisplay = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (isNaN(date)) return value;
        return date.toLocaleDateString("en-GB").replace(/\//g, "-");
    };

    // Converts "HH:mm" or "H:mm" to "h:mm AM/PM"
    const formatTime12Hour = (time) => {
        if (!time) return '';
        // If already in "h:mm AM/PM" format, return as is
        if (/^\d{1,2}:\d{2} (AM|PM)$/i.test(time)) {
            return time;
        }
        const [hoursStr, minutesStr] = time.split(':');
        if (!hoursStr || !minutesStr) return 'No Records Found';
        let hours = parseInt(hoursStr, 10);
        const minutes = minutesStr.padStart(2, '0');
        if (isNaN(hours) || isNaN(parseInt(minutes, 10))) return 'No Records Found';
        const amPm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${amPm}`;
    };

    // Converts total minutes into "X hr Y mins" format
    const formatMinutesToHours = (minutes) => {
        if (minutes == null || minutes === 0) return ''; // Hide if null or 0

        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;

        if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} mins`;
        if (hrs > 0) return `${hrs} hr`;
        return `${mins} mins`;
    };

    // Converts decimal hours (e.g., 8.5) to "X hr Y mins"
    const formatHoursFromDecimal = (hours) => {
        if (hours == null || hours === 0 || isNaN(hours)) return '';

        const totalMinutes = Math.round(Number(hours) * 60);
        const hrs = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} mins`;
        if (hrs > 0) return `${hrs} hr`;
        return `${mins} mins`;
    };

    const columns = [
        {
            field: "attendance_date",
            headerName: "Date",
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return null;
                const formattedDate = formatDateDisplay(params.value);
                return (
                    <Tooltip title={formattedDate}>
                        <span className="cell-wrap">{formattedDate}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "user_name",
            headerName: "Name",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "shift_name",
            headerName: "Shift Name",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "in_time",
            headerName: "In Time",
            flex: 1,
            renderCell: (params) => {
                const formatTimeDifference = (minutes) => {
                    if (minutes === 0 || isNaN(minutes)) return null;
                    const isNegative = minutes < 0;
                    const absMinutes = Math.abs(minutes);
                    if (absMinutes < 60) {
                        return `${isNegative ? "-" : ""}${absMinutes} mins`;
                    } else {
                        const hours = Math.floor(absMinutes / 60);
                        const mins = absMinutes % 60;
                        return `${isNegative ? "-" : ""}${hours}hr${mins > 0 ? ` ${mins}mins` : ""}`;
                    }
                };

                const formattedDiff = formatTimeDifference(params.row.time_difference);

                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Tooltip title={params.value}>
                            <span className="cell-wrap">{formatTime12Hour(params.value)}</span>
                        </Tooltip>
                        {formattedDiff && (
                            <span
                                style={{
                                    color: "#dc3545",
                                    fontSize: "0.75rem",
                                    fontWeight: "500",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                ({formattedDiff})
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            field: "late_time",
            headerName: "Late Check In",
            flex: 1,
            renderCell: (params) => {
                const formatted = formatMinutesToHours(params.value);
                return (
                    <Tooltip title={formatted}>
                        <span style={{ color: "red" }}>
                            {formatted ? `- ${formatted}` : ""}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "out_time",
            headerName: "Out Time",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={formatTime12Hour(params.value)}>
                    <span className="cell-wrap">{formatTime12Hour(params.value)}</span>
                </Tooltip>
            ),
        },
        {
            field: "early_time",
            headerName: "Early Check Out",
            flex: 1,
            renderCell: (params) => {
                const formatted = formatMinutesToHours(params.value);
                return (
                    <Tooltip title={formatted}>
                        <span style={{ color: "red" }}>
                            {formatted ? `- ${formatted}` : ""}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "no_of_hours",
            headerName: "No Of Hrs",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={formatMinutesToHours(params.value)}>
                    <span className="cell-wrap">{formatMinutesToHours(params.value)}</span>
                </Tooltip>
            ),
        },
        // {
        //     field: "over_time",
        //     headerName: "Over Time",
        //     flex: 1,
        //     renderCell: (params) => (
        //         <Tooltip title={formatMinutesToHours(params.value)}>
        //             <span className="cell-wrap">{formatMinutesToHours(params.value)}</span>
        //         </Tooltip>
        //     ),
        // },
        {
            field: "attendance_type_name",
            headerName: "Attendance Type",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
    ];

    const searchableColumns = [
        (row) => row.user_name || "",
        (row) => (row.attendance_date) || "",
        (row) => formatTime12Hour(row.in_time) || "",
        (row) => formatTime12Hour(row.out_time) || "",
        (row) => formatMinutesToHours(row.early_time) || "",
        (row) => formatMinutesToHours(row.late_time) || "",
        (row) => formatMinutesToHours(row.over_time) || "",
        (row) => formatMinutesToHours(row.no_of_hours) || "",
        (row) => row.attendance_type_name || "",
        (row) => row.shift_name || "",
    ];

    const normalizedQuery = (searchQuery || '').toLowerCase().replace(/[-\s]+/g, '');

    const filteredRecords = (levels || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            if (typeof value !== 'string') return false;
            const stringValue = value.toLowerCase().replace(/[-\s]+/g, '');
            return stringValue.includes(normalizedQuery);
        })
    );

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        setForm((prevForm) => ({
            ...prevForm,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = {
            userid: form.userid || null,
            attendance_date: form.attendance_date || null,
            from_date: form.from_date || null,
            to_date: form.to_date || null,
            shift_id: form.shift_id || null,
            in_time: form.in_time || null,
            out_time: form.out_time || null,
            school_id: userObj.school_id,
            attendance_type_id: form.attendance_type_id || null,
            action: 'FILTER',
        };

        try {
            const response = await axios.post(baseUrl + "/employeeattendance/", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = response.data;

            if (data && typeof data === "object" && data.message === "No data found for action: FILTER") {
                setLevels([]);
            } else {
                setLevels(data);
            }
        } catch (error) {
            console.error("Full error object:", error);
            setLevels([]);
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setForm({
            userid: "",
            attendance_date: "",
            attendance_type_id: "",
            shift_id: "",
            in_time: "",
            out_time: "",
            from_date: "",
            to_date: ""
        });
        fetchData();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // ---------- NEW: EXPORT FUNCTIONS (like EmployeeAttendanceReport) ----------

    const exportToExcel = () => {
        if (!filteredRecords.length) {
            toast.error("No data available to export.");
            return;
        }

        const exportData = filteredRecords.map((row) => ({
            "Date": formatDateDisplay(row.attendance_date) || "-",
            "Name": row.user_name || "-",
            "Shift Name": row.shift_name || "-",
            "In Time": formatTime12Hour(row.in_time) || "-",
            "Late Check In": formatMinutesToHours(row.late_time) || "-",
            "Out Time": formatTime12Hour(row.out_time) || "-",
            "Early Check Out": formatMinutesToHours(row.early_time) || "-",
            "No Of Hrs": formatMinutesToHours(row.no_of_hours) || "-",
            "Attendance Type": row.attendance_type_name || "-",
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employee-Attendance");

        const todayStr = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
        XLSX.writeFile(wb, `employee_attendance_${todayStr}.xlsx`);
    };

    const exportToPDF = () => {
        if (!filteredRecords.length) {
            toast.error("No data available to export.");
            return;
        }

        const doc = new jsPDF();
        doc.text("Employee Attendance", 14, 10);

        const tableColumn = [
            "Date",
            "Name",
            "Shift Name",
            "In Time",
            "Late Check In",
            "Out Time",
            "Early Check Out",
            "No Of Hrs",
            "Attendance Type",
        ];

        const tableRows = filteredRecords.map((row) => [
            formatDateDisplay(row.attendance_date) || "-",
            row.user_name || "-",
            row.shift_name || "-",
            formatTime12Hour(row.in_time) || "-",
            formatMinutesToHours(row.late_time) || "-",
            formatTime12Hour(row.out_time) || "-",
            formatMinutesToHours(row.early_time) || "-",
            formatMinutesToHours(row.no_of_hours) || "-",
            row.attendance_type_name || "-",
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [0, 123, 255] },
        });

        const todayStr = new Date().toISOString().slice(0, 10);
        doc.save(`employee_attendance_${todayStr}.pdf`);
    };

    // --------------------------------------------------------------------------

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Employee Attendance</h6>
                    </div>

                    <div
                        className="d-flex justify-content-center"
                        style={{ flex: 1 }}
                    >
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput mx-3"
                            onChange={handleSearchChange}
                        />
                    </div>

                    {/* NEW: Excel & PDF buttons on the right, like the report screen */}
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                        <Button
                            className="btnMain"
                            variant="success"
                            onClick={exportToExcel}
                            size="sm"
                        >
                            <img
                                src={excelIcon}
                                alt="Download Excel"
                                style={{ width: "20px" }}
                            />
                        </Button>
                        <Button
                            className="btnMain"
                            variant="danger"
                            onClick={exportToPDF}
                            size="sm"
                        >
                            <FaFilePdf style={{ marginRight: "5px" }} />
                        </Button>
                    </div>
                </div>
            </div>

            <div
                className="filterFormContainer mb-3 bg-light rounded"
                style={{ padding: "8px 10px" }}
            >
                <Form
                    onSubmit={handleSubmit}
                    className="d-flex flex-wrap"
                    style={{
                        gap: "8px",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                    }}
                >
                    {/* LEFT SIDE: Filters */}
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-end",
                            gap: "8px",
                            flex: 1,
                        }}
                    >
                        <Form.Group
                            controlId="userid"
                            style={{
                                minWidth: "150px",
                                maxWidth: "180px",
                            }}
                        >
                            <Form.Select
                                size="sm"
                                className="form-select-sm"
                                value={form.userid}
                                onChange={(e) =>
                                    setForm({ ...form, userid: e.target.value })
                                }
                                style={{
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "0.8rem",
                                    height: "calc(1.4em + 0.4rem + 2px)",
                                }}
                            >
                                <option value="" >Select Name</option>
                                {(users || [])
                                    .filter((user) => user.status === "Active")
                                    .map((user) => (
                                        <option key={user.emp_id} value={user.emp_id}>
                                            {user.emp_name}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group
                            controlId="from_date"
                            style={{ minWidth: "150px" }}
                        >
                            <Form.Control
                                type="date"
                                size="sm"
                                value={form.from_date}
                                onChange={(e) =>
                                    setForm({ ...form, from_date: e.target.value })
                                }
                                style={{
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "0.8rem",
                                    height: "calc(1.4em + 0.4rem + 2px)",
                                }}
                            />
                        </Form.Group>

                        <Form.Group controlId="to_date" style={{ minWidth: "150px" }}>
                            <Form.Control
                                type="date"
                                size="sm"
                                value={form.to_date}
                                onChange={(e) =>
                                    setForm({ ...form, to_date: e.target.value })
                                }
                                style={{
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "0.8rem",
                                    height: "calc(1.4em + 0.4rem + 2px)",
                                }}
                            />
                        </Form.Group>

                        <Form.Group
                            controlId="attendance_type_id"
                            style={{ minWidth: "180px" }}
                        >
                            <Form.Select
                                size="sm"
                                value={form.attendance_type_id}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        attendance_type_id: e.target.value,
                                    })
                                }
                                style={{
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "0.8rem",
                                    height: "calc(1.4em + 0.4rem + 2px)",
                                }}
                            >
                                <option value="" >Select Attendance Type</option>
                                {(attendancetypes || [])
                                    .filter(
                                        (attendancetype) =>
                                            attendancetype.is_active === "Active" ||
                                            attendancetype.is_active === undefined
                                    )
                                    .map((attendancetype) => (
                                        <option
                                            key={attendancetype.attendance_type_id}
                                            value={attendancetype.attendance_type_id}
                                        >
                                            {attendancetype.attendance_type_name}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group
                            controlId="shift_id"
                            style={{ minWidth: "180px" }}
                        >
                            <Form.Select
                                size="sm"
                                value={form.shift_id}
                                onChange={(e) =>
                                    setForm({ ...form, shift_id: e.target.value })
                                }
                                style={{
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "0.8rem",
                                    height: "calc(1.4em + 0.4rem + 2px)",
                                }}
                            >
                                <option value="" >Select Shift Name</option>
                                {(shifts || [])
                                    .filter((shift) => shift.is_active === "Active")
                                    .map((shift) => (
                                        <option key={shift.shift_id} value={shift.shift_id}>
                                            {shift.shift_name}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>
                    </div>

                    {/* RIGHT SIDE: Buttons (same line as filters when space allows) */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Button
                            type="submit"
                            variant="primary"
                            className="btn-success primaryBtn"
                            size="sm"
                            style={{
                                padding: "0.2rem 0.6rem",
                                fontSize: "0.8rem",
                                lineHeight: 1.2,
                            }}
                        >
                            Submit
                        </Button>
                        <button
                            type="button"
                            className="btn btn-danger secondaryBtn btn-sm"
                            onClick={handleReset}
                            style={{
                                padding: "0.2rem 0.6rem",
                                fontSize: "0.8rem",
                                lineHeight: 1.2,
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </Form>
            </div>

            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={
                                Array.isArray(filteredRecords) && filteredRecords.length > 0
                                    ? filteredRecords
                                    : []
                            }
                            columns={columns}
                            getRowId={(row) => row.emp_attend_id || `${row.userid}_${row.attendance_date}`}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={400}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Employeeattendance;
